const BUILD_ID_STORAGE_KEY = 'streamliner:build-id';
const BUILD_ID_RELOAD_KEY = 'streamliner:build-id-reload';

type BuildIdRefreshOptions = {
  buildId?: string;
};

const readStorageValue = (key: string): string | undefined => {
  try {
    const value = window.localStorage.getItem(key);
    if (value) return value;
  } catch {
    // ignore
  }
  try {
    return window.sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeStorageValue = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
    return;
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const clearReloadFlag = () => {
  try {
    window.sessionStorage.removeItem(BUILD_ID_RELOAD_KEY);
  } catch {
    // ignore
  }
};

const hasReloaded = () => {
  try {
    return window.sessionStorage.getItem(BUILD_ID_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
};

const markReloaded = () => {
  try {
    window.sessionStorage.setItem(BUILD_ID_RELOAD_KEY, '1');
  } catch {
    // ignore
  }
};

const triggerReload = () => {
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  const serviceWorker = window.navigator?.serviceWorker;
  if (!serviceWorker || typeof serviceWorker.addEventListener !== 'function') {
    reload();
    return;
  }

  serviceWorker.addEventListener('controllerchange', reload, { once: true });

  const registrationPromise =
    typeof serviceWorker.getRegistration === 'function'
      ? serviceWorker.getRegistration()
      : Promise.resolve(undefined);

  registrationPromise
    .then(registration => {
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        return;
      }
      if (registration?.update) {
        registration.update().catch(() => undefined);
      }
    })
    .finally(() => {
      window.setTimeout(reload, 2000);
    });
};

export const setupBuildIdRefresh = ({ buildId }: BuildIdRefreshOptions = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  const resolvedBuildId = typeof buildId === 'string' ? buildId : '';
  if (!resolvedBuildId) {
    return;
  }

  const storedBuildId = readStorageValue(BUILD_ID_STORAGE_KEY);

  if (storedBuildId && storedBuildId !== resolvedBuildId && !hasReloaded()) {
    markReloaded();
    triggerReload();
    return;
  }

  writeStorageValue(BUILD_ID_STORAGE_KEY, resolvedBuildId);
  clearReloadFlag();
};
